import { readChat } from "@/api/chat";
import { getJobseekerMeetingByJobId } from "@/api/jobseeker";
import client, { client2 } from "@/config/axios-wrapper";
import { Meeting } from "@/types/meeting";
import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";

interface Chat {
  id: string;
  unread_count: number;
  organization: { id: string; name: string; logo: string; logo_thumbnail_url: string };
  job: {
    id: string;
    alt_id: string;
    title: string;
    address: { line1: string; line2: string; city: string; zip_code: string; country: string };
  };
  jobseeker: {
    id: string;
    full_name: string;
    job_title: string;
    photo: string;
    photo_thumbnail_url: string;
  };
  last_message?: {
    id: string;
    sender: string;
    sender_id: string;
    type: string;
    text: string;
    files: Array<{ name: string; mimetype: string; url: string }>;
    created_at: string;
    updated_at: string;
  };
  ended: boolean;
  created_at: string;
}

interface ChatState {
  list: Chat[];
  unread_list: Chat[];
  total: number;
  skip: number;
  unread: number;
  meeting: Meeting;
}

const initialState: ChatState = {
  list: [],
  unread_list: [],
  total: 0,
  skip: 0,
  unread: 0,
  meeting: null,
};

export const getChatMeeting = createAsyncThunk(
  "chat/getChatMeeting",
  async ({ jobseekerId, jobId }: { jobseekerId: string; jobId: string }) => {
    const data = await getJobseekerMeetingByJobId(jobseekerId);

    const meetings = data?.meetings || [];
    const filteredMeetings = meetings.filter((meeting) => meeting.job.id === jobId);
    return filteredMeetings.length ? filteredMeetings[0] : null;
  },
);

export const readChatMessage = createAsyncThunk("chat/readChatMessage", async (chatId: string) => {
  await readChat(chatId);
  return chatId;
});

export const getChats = createAsyncThunk(
  "chat/getChats",
  async (
    {
      searchQuery = "",
      organizationId = null,
      userId = null,
      append = false,
    }: {
      searchQuery?: string;
      organizationId?: string | null;
      userId?: string | null;
      append?: boolean;
    },
    { getState },
  ) => {
    const { chat } = getState() as { chat: ChatState };
    const { list, skip = 0, total } = chat;
    if (list.length === 0 || total > skip) {
      const response = await client.get(
        `${
          organizationId ? `organizations/${organizationId}` : `users/${userId}`
        }/chats?skip=${skip}&limit=20&q=${searchQuery}`,
      );
      return { data: response.data, append: append };
    }
  },
);

export const getUnreadChats = createAsyncThunk(
  "chat/getUnreadChats",
  async (
    {
      organizationId,
      userId,
      searchQuery = "",
      skip = 0,
    }: { organizationId: string; skip?: number; userId?: string; searchQuery?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await client.get(
        `${
          organizationId ? `organizations/${organizationId}` : `users/${userId}`
        }/chats?status=unread&&limit=100&&skip=0&&q=${searchQuery}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch unread chats");
    }
  },
);

export const getChatStats = createAsyncThunk("chat/getChatStats", async () => {
  const response = await client2.get("chats/stats");
  return { data: response.data };
});

export const getUserChats = createAsyncThunk(
  "chat/getUserChats",
  async ({ q = "", userId = null }: { q?: string; userId?: string | null }, { getState }) => {
    const { chat } = getState() as { chat: ChatState };
    const { list, skip = 0, total } = chat;

    if (list.length === 0 || total > skip) {
      const response = await client.get(`users/${userId}/chats?skip=${skip}&limit=20&q=${q}`);
      return { data: response.data, append: skip > 0 };
    }
  },
);

export const getOrganizationChats = createAsyncThunk(
  "chat/getOrganizationChats",
  async (
    { q = "", organizationId = null }: { q?: string; organizationId?: string },
    { getState },
  ) => {
    const { chat } = getState() as { chat: ChatState };
    const { list, skip = 0, total } = chat;

    if (list.length === 0 || total > skip) {
      const response = await client.get(
        `organizations/${organizationId}/chats?skip=${skip}&limit=20&q=${q}`,
      );
      return { data: response.data, append: skip > 0 };
    }
  },
);

export const updateChats = createAsyncThunk(
  "chat/updateChats",
  async ({
    chatToUpdate,
    selectedChat,
    currentUserType,
  }: {
    chatToUpdate: any;
    selectedChat: string;
    currentUserType: string;
  }) => {
    return { chatToUpdate, selectedChat, currentUserType };
  }
);

export const resetChatSkip = createAsyncThunk("chat/resetChatSkip", async (_, { dispatch }) => {
  dispatch(chatSlice.actions.setSkip(0));
});

export const clearChats = createAsyncThunk(
  "notification/clearNotifications",
  async (_, { dispatch }) => {
    dispatch(chatSlice.actions.resetChats());
  },
);

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    resetChats(state) {
      state.list = initialState.list;
      state.meeting = initialState.meeting;
      state.skip = initialState.skip;
      state.total = initialState.total;
      state.unread = initialState.unread;
    },
    setSkip(state, action: PayloadAction<number>) {
      state.skip = action.payload;
    },
    updateChatEnded(state, action: PayloadAction<{ chatId: string; ended: boolean }>) {
      const { chatId, ended } = action.payload;
      const chatIndex = state.list.findIndex((chat) => chat.id === chatId);
      if (chatIndex !== -1) {
        state.list[chatIndex].ended = ended;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChatMeeting.fulfilled, (state, action: PayloadAction<any>) => {
        state.meeting = action.payload;
      })
      .addCase(readChatMessage.fulfilled, (state, action: PayloadAction<string>) => {
        const index = state.list.findIndex((item) => item.id === action.payload);
        if (index !== -1) {
          const unreadCount = state.list[index].unread_count || 0;

          // Reset unread count for the chat
          state.list[index].unread_count = 0;

          // Subtract that amount from global unread (safely)
          state.unread = Math.max(0, state.unread - unreadCount);
        }
      })
      .addCase(
        getChats.fulfilled,
        (state, action: PayloadAction<{ data: any; append: boolean }>) => {
          if (action.payload) {
            const newChats = action.payload.data?.chats || [];
            if (action.payload.append) {
              // Merge with existing chats if appending
              const existingChatIds = new Set(state.list.map((chat) => chat.id));
              const mergedChats = [
                ...state.list,
                ...newChats.filter((chat) => !existingChatIds.has(chat.id)),
              ];

              state.list = mergedChats;
              state.skip = mergedChats.length;
            } else {
              // Replace list when not appending
              state.list = newChats;
              state.skip = newChats.length;
            }
            state.total = action.payload.data?.total || 0;
          }
        },
      )
      .addCase(getUnreadChats.fulfilled, (state, action: PayloadAction<any>) => {
        state.unread_list = action.payload.chats || [];
        state.unread = action.payload.total || 0;
      })
      .addCase(getChatStats.fulfilled, (state, action: PayloadAction<{ data: any }>) => {
        state.unread = action.payload?.data?.result?.unread_chat_count || 0;
      })
      .addCase(
        getUserChats.fulfilled,
        (state, action: PayloadAction<{ data: any; append: boolean }>) => {
          if (action.payload.append) {
            state.list = [...state.list, ...action.payload.data.chats];
          } else {
            state.list = action.payload.data.chats;
          }
          state.total = action.payload.data.total;
          state.skip += 20;
          state.unread = state.list.filter((chat) => chat.unread_count > 0).length;
        },
      )
      .addCase(
        getOrganizationChats.fulfilled,
        (state, action: PayloadAction<{ data: any; append: boolean }>) => {
          if (action.payload.append) {
            state.list = [...state.list, ...action.payload.data.chats];
          } else {
            state.list = action.payload.data.chats;
          }
          state.total = action.payload.data.total;
          state.skip += 20;
          // state.unread = state.list.filter((chat) => chat.unread_count > 0).length;
        },
      )
      .addCase(
        updateChats.fulfilled,
        (
          state,
          action: PayloadAction<{
            chatToUpdate: any;
            selectedChat: string;
            currentUserType: string;
          }>,
        ) => {
          const { list, unread } = state;
          const { chatToUpdate, selectedChat, currentUserType } = action.payload;
          let newList = [...list];
          let updatedUnread = unread;

          const index = list.findIndex((item) => item.id === chatToUpdate.chat_id);
          if (index !== -1) {
            const chat = { ...list[index] };

            // Update last message
            chat.last_message = {
              ...chat.last_message,
              sender_id: chatToUpdate.sender_id,
              text: chatToUpdate.text,
            };
            console.log(chatToUpdate)
            // Only increment if the message is 'unread' AND from a different user type
            if (chatToUpdate.status === "unread" && chatToUpdate.sender_ref !== currentUserType) {
              updatedUnread += 1;
              chat.unread_count = (chat.unread_count || 0) + 1;
            }

            newList[index] = chat;
          }

          state.list = newList;
          state.unread = updatedUnread;
        },
      );
  },
});

export const { resetChats, setSkip, updateChatEnded } = chatSlice.actions;
export default chatSlice.reducer;
